import { useState } from 'react';
import { FiX } from 'react-icons/fi';
import { apiImportBooksXlsx, apiDownloadBooksTemplate } from '../../api';
import { confirmAdminAction } from '../../utils/adminConfirm';

const Stat = ({ label, value, color = 'text-black' }) => (
  <div className="border rounded px-3 py-2">
    <div className="text-xs text-black">{label}</div>
    <div className={`text-lg font-semibold ${color}`}>{value}</div>
  </div>
);

export const ImportModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const creations = preview?.creations || [];
  const categoryCreations = preview?.categoryCreations || [];
  const updateCount = preview?.changes?.length || 0;
  const plannedCount = creations.length + updateCount;
  const previewRows = [
    ...(preview?.changes || []).map((change) => ({ ...change, action: 'Update' })),
    ...creations.map((creation) => ({ ...creation, action: 'Create' })),
  ].sort((a, b) => a.row - b.row);

  const handleFile = (e) => {
    const selected = e.target.files[0] || null;
    if (selected && selected.size > 25 * 1024 * 1024) {
      setFile(null);
      setPreview(null);
      setError('File is too large. Maximum size is 25 MB.');
      e.target.value = '';
      return;
    }
    setFile(selected);
    setPreview(null);
    setError('');
  };

  const downloadTemplate = async () => {
    if (downloading) return;
    setDownloading(true);
    setError('');
    try {
      await apiDownloadBooksTemplate();
    } catch (err) {
      setError(err.message || 'Excel template download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const runPreview = async () => {
    if (!file || loading || applying) return;
    setLoading(true);
    setPreview(null);
    setError('');
    try {
      const res = await apiImportBooksXlsx(file, { apply: false });
      setPreview(res);
    } catch (err) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!file || !preview || !plannedCount || loading || applying) return;
    setApplying(true);
    setError('');
    try {
      const confirmed = await confirmAdminAction(`Create ${creations.length} books and update ${updateCount} books?${categoryCreations.length ? ` ${categoryCreations.length} new ${categoryCreations.length === 1 ? 'category' : 'categories'} will also be created.` : ''} ${preview.rejected || 0} invalid rows will be skipped.`, { title: 'Apply book import', confirmLabel: 'Apply changes' });
      if (!confirmed) return;
      await apiImportBooksXlsx(file, { apply: true });
      onImported();
    } catch (err) {
      setError(err.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">Import Books from Excel</h2>
          <button onClick={onClose} aria-label="Close import" disabled={loading || applying} className="text-black hover:text-black disabled:opacity-50">
            <FiX size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded">
            <p className="font-medium mb-1">How it works</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Click <b>Download Excel Template</b> for supported headers and five sample records. Use <b>Export Excel</b> to edit current books.</li>
              <li>Books are matched by <b>isbn13 only</b>. ID, ISBN-10, SKU, title, and author are not used for matching. Rows without ISBN-13 are skipped.</li>
              <li>Matched books are updated. Unmatched rows create new books with generated database IDs.</li>
              <li>New books require <b>isbn13</b>, <b>title</b>, <b>author</b>, and <b>stock</b>, plus either <b>price</b> or <b>originalPrice</b> with <b>discount</b>. Missing categories are created automatically when you Apply, then assigned to the books.</li>
              <li>Only columns present in your Excel are updated. Empty cells other than required ISBN-13 are skipped (existing values stay).</li>
              <li>All rows with a repeated ISBN-13 are skipped. Repeated ISBN-10 values are allowed. Rows with invalid values are skipped entirely; other valid rows can be applied.</li>
              <li>When a discount is supplied, selling price = MRP × (1 − discount ÷ 100), rounded to two decimals, for each currency. It takes priority over the supplied price. If discount is blank, it is calculated from the prices.</li>
              <li>Imports ignore image and cover URL columns. Existing covers stay unchanged; manage images through Edit Book or the separate Cover Sync application.</li>
              <li>Always click <b>Preview</b> first — nothing is saved until you click Apply.</li>
            </ul>
          </div>

          <div className="text-sm space-y-2">
            <button
              onClick={downloadTemplate}
              disabled={downloading || loading || applying}
              className="px-4 py-2 border rounded font-medium disabled:opacity-50"
            >
              {downloading ? 'Downloading...' : 'Download Excel Template'}
            </button>
            <p className="text-xs text-black">The template contains all supported headers and 5 sample records in the Books sheet, plus an Instructions sheet with accepted formats. Replace or delete the samples, save as .xlsx, then choose that file below. Leave id blank for new books. Sample records are inactive; set isActive to TRUE when your books are ready for the shop.</p>
          </div>

          <div>
            <label htmlFor="xlsx-file" className="block text-sm font-medium text-black mb-2">
              Choose .xlsx / .xls / .csv file
            </label>
            <input
              id="xlsx-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              disabled={loading || applying}
              onChange={handleFile}
              className="block w-full text-sm text-black file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={runPreview}
              disabled={!file || loading || applying}
              className="px-4 py-2 bg-gray-800 text-white rounded text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            >
              {loading ? 'Analysing…' : 'Preview Changes'}
            </button>
            {preview && plannedCount > 0 && (
              <button
                onClick={runApply}
                disabled={applying || loading}
                className="px-4 py-2 bg-orange-600 text-white rounded text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {applying ? 'Applying…' : `Apply (${plannedCount})`}
              </button>
            )}
          </div>

          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <Stat label="Total rows" value={preview.totalRows} />
                <Stat label="Matched" value={preview.matched} color="text-green-700" />
                <Stat label="Unmatched" value={preview.unmatched} color="text-red-600" />
                <Stat label="Rejected rows" value={preview.rejected || 0} color="text-red-600" />
                <Stat label="Will create" value={creations.length} color="text-green-700" />
                <Stat label="Will update" value={updateCount} color="text-orange-700" />
                <Stat label="Categories to create" value={categoryCreations.length} color="text-blue-700" />
              </div>

              {categoryCreations.length > 0 && (
                <p className="text-xs text-black"><b>New categories:</b> {categoryCreations.map((category) => category.name).join(', ')}</p>
              )}

              {Object.keys(preview.mappedColumns).length > 0 && (
                <p className="text-xs text-black">
                  <b>Detected columns:</b>{' '}
                  {Object.entries(preview.mappedColumns)
                    .map(([excel, db]) => `${excel}→${db}`)
                    .join(', ')}
                </p>
              )}

              {plannedCount > 0 && (
                <div className="border rounded overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-black">
                    Changes preview (first 20)
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-black">
                        <tr>
                          <th className="px-2 py-1 text-left">Row</th>
                          <th className="px-2 py-1 text-left">Action</th>
                          <th className="px-2 py-1 text-left">Book</th>
                          <th className="px-2 py-1 text-left">Changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.slice(0, 20).map((c) => (
                          <tr key={c.row} className="border-t">
                            <td className="px-2 py-1 text-black">{c.row}</td>
                            <td className="px-2 py-1">{c.action}</td>
                            <td className="px-2 py-1">{c.title}</td>
                            <td className="px-2 py-1">
                              {Object.entries(c.diff).map(([k, v]) => (
                                <div key={k} className="text-black">
                                  <b>{k}:</b>{' '}
                                  <span className="text-red-600 line-through">
                                    {String(v.from ?? '')}
                                  </span>{' '}
                                  → <span className="text-green-700">{String(v.to)}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(preview.errors || []).length > 0 && (
                <div className="border border-red-200 rounded overflow-hidden">
                  <div className="px-3 py-2 bg-red-50 text-xs font-medium text-red-700">
                    Warnings ({preview.errors.length})
                  </div>
                  <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                    {preview.errors.slice(0, 20).map((e) => (
                      <div key={`${e.row}-${e.reason}`} className="text-xs text-red-700">
                        Row {e.row}: {e.reason} {e.title && `(${e.title})`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
